import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAgencyBookings } from "../../services/agencyService";
import { adminBookingLogin } from "../../services/adminAuth";
import { bookingActions } from "../../redux/slices/bookingSlice";
import { loaderActions } from "../../redux/slices/loadingSlice";
import { useAppDispatch } from "../../redux/store";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
} from "@mui/material";

interface BookingResult {
  bookingNumber: string;
  orgunit: string;
  status: string;
}

const AgencyMidocoPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [bookings, setBookings] = useState<BookingResult[]>([]);
  const agencyId = sessionStorage.getItem("agencyId");

  useEffect(() => {
    const isAuth = sessionStorage.getItem("agencyAuthenticated");
    if (isAuth !== "true" || !agencyId) {
      navigate("/agency");
      return;
    }

    loadBookings();
  }, []);

  const loadBookings = async () => {
    if (!agencyId) return;

    dispatch(loaderActions.showLoader());
    const data = await fetchAgencyBookings(agencyId);
    dispatch(loaderActions.hideLoader());

    if (data) {
      setBookings(data);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("agencyAuthenticated");
    sessionStorage.removeItem("agencyId");
    navigate("/agency");
  };

  const handleViewBooking = async (bookingNumber: string) => {
    dispatch(loaderActions.showLoader());
    const response = await adminBookingLogin(bookingNumber);
    
    if (response) {
      sessionStorage.setItem("lmxBooking", JSON.stringify(response));
      dispatch(bookingActions.setBookingData(response));
      dispatch(loaderActions.hideLoader());
      
      if (response.isCanceled || response.isExpired) {
        navigate("/info");
      } else {
        navigate("/booking-details");
      }
    } else {
      dispatch(loaderActions.hideLoader());
      alert("Failed to fetch booking details.");
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow">
        <Typography variant="h5" component="h1" className="font-bold text-gray-800">
          Agency Dashboard - Bookings for {agencyId}
        </Typography>
        <Button variant="outlined" color="error" onClick={handleLogout}>
          Logout
        </Button>
      </div>

      <TableContainer component={Paper} className="shadow-lg rounded-lg overflow-hidden">
        <Table sx={{ minWidth: 650 }} aria-label="bookings table">
          <TableHead className="bg-gray-100">
            <TableRow>
              <TableCell className="font-bold">Booking Number</TableCell>
              <TableCell className="font-bold">Org Unit</TableCell>
              <TableCell className="font-bold">Status</TableCell>
              <TableCell className="font-bold align-right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.length > 0 ? (
              bookings.map((booking, index) => (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <TableCell component="th" scope="row">
                    {booking.bookingNumber}
                  </TableCell>
                  <TableCell>{booking.orgunit}</TableCell>
                  <TableCell>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        booking.status === "OK"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {booking.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      size="small"
                      onClick={() => handleViewBooking(booking.bookingNumber)}
                    >
                      View Booking
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" className="py-8 text-gray-500">
                  No bookings found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default AgencyMidocoPage;
